import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import GoalTracker from '../ui/GoalTracker';
import { 
  Leaf, Plus, Droplets, Mountain, DollarSign, Trash2, Loader2, 
  TrendingUp, Egg, Milk, TreeDeciduous, Gauge, Sprout, Info, Target
} from 'lucide-react';
import { toast } from 'sonner';
import { todayLocal } from '../../utils/dates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const metricTypes = [
  { id: 'water_usage', name: 'Water Usage', icon: Droplets, unit: 'gallons' },
  { id: 'soil_ph', name: 'Soil pH', icon: Mountain, unit: 'pH' },
  { id: 'input_cost', name: 'Feed/Input Cost', icon: DollarSign, unit: 'USD' },
  { id: 'compost_added', name: 'Compost Added', icon: Leaf, unit: 'lbs' },
  { id: 'fuel_usage', name: 'Fuel Usage', icon: Droplets, unit: 'gallons' },
];

const SustainabilityTracker = () => {
  const { getAuthHeader } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [summary, setSummary] = useState([]);
  const [calculated, setCalculated] = useState(null);
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [formData, setFormData] = useState({
    metric_type: 'water_usage',
    value: '',
    notes: '',
    date: todayLocal()
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [metricsRes, summaryRes, calculatedRes, goalsRes] = await Promise.all([
        axios.get(`${API_URL}/api/sustainability`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/sustainability/summary`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/sustainability/calculated`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/goals`, { headers: getAuthHeader() })
      ]);
      setMetrics(metricsRes.data.metrics || []);
      setSummary(summaryRes.data.summary || []);
      setCalculated(calculatedRes.data);
      setGoals(goalsRes.data.goals || []);
    } catch (error) {
      console.error('Failed to fetch sustainability data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchGoals = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/goals`, { headers: getAuthHeader() });
      setGoals(res.data.goals || []);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    }
  };

  const handleCreateGoal = async (goalData) => {
    try {
      await axios.post(`${API_URL}/api/goals`, goalData, { 
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } 
      });
      toast.success('Goal created!');
      fetchGoals();
    } catch (error) {
      console.error('Failed to create goal:', error);
      toast.error('Failed to create goal');
    }
  };

  const handleEditGoal = async (goalId, goalData) => {
    try {
      await axios.put(`${API_URL}/api/goals/${goalId}`, goalData, { 
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } 
      });
      toast.success('Goal updated!');
      fetchGoals();
    } catch (error) {
      console.error('Failed to update goal:', error);
      toast.error('Failed to update goal');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      await axios.delete(`${API_URL}/api/goals/${goalId}`, { headers: getAuthHeader() });
      toast.success('Goal deleted');
      fetchGoals();
    } catch (error) {
      console.error('Failed to delete goal:', error);
      toast.error('Failed to delete goal');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async () => {
    if (!formData.value) {
      toast.error('Please enter a value');
      return;
    }

    setSaving(true);
    const selectedType = metricTypes.find(t => t.id === formData.metric_type);
    
    try {
      await axios.post(
        `${API_URL}/api/sustainability`,
        {
          metric_type: formData.metric_type,
          value: parseFloat(formData.value),
          unit: selectedType?.unit || 'units',
          notes: formData.notes || null,
          date: formData.date
        },
        { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } }
      );
      
      toast.success('Metric recorded');
      setIsDialogOpen(false);
      setFormData({
        metric_type: 'water_usage',
        value: '',
        notes: '',
        date: todayLocal()
      });
      fetchData();
    } catch (error) {
      console.error('Failed to save metric:', error);
      toast.error('Failed to save metric');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/sustainability/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete entry');
    }
  };

  const getMetricInfo = (typeId) => {
    return metricTypes.find(t => t.id === typeId) || { name: typeId, icon: Leaf };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-500';
    if (score >= 50) return 'text-emerald-500';
    if (score >= 25) return 'text-yellow-500';
    return 'text-orange-500';
  };

  const getScoreBg = (score) => {
    if (score >= 75) return 'bg-green-500/20';
    if (score >= 50) return 'bg-emerald-500/20';
    if (score >= 25) return 'bg-yellow-500/20';
    return 'bg-orange-500/20';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sustainability-tracker">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground">
            Sustainability Tracker
          </h1>
          <p className="text-muted-foreground mt-1">
            Track and analyze your farm&apos;s environmental impact
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => { window.location.href = '/app/reports?template=grant_application&include=sustainability'; }}
            data-testid="export-to-report-sustain-btn"
            className="hidden sm:inline-flex"
          >
            Export to Report
          </Button>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-metric-btn">
              <Plus className="w-4 h-4 mr-2" />
              Log Entry
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Log Metric</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Metric Type</Label>
                <Select 
                  value={formData.metric_type} 
                  onValueChange={(v) => setFormData({ ...formData, metric_type: v })}
                >
                  <SelectTrigger data-testid="metric-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {metricTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.name} ({type.unit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="value">Value</Label>
                <Input
                  id="value"
                  type="number"
                  step="0.01"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder={`Enter ${getMetricInfo(formData.metric_type).unit}`}
                  className="input-farm"
                  data-testid="metric-value-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="input-farm"
                  data-testid="metric-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Input
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="e.g., South pasture irrigation"
                  className="input-farm"
                  data-testid="metric-notes-input"
                />
              </div>
              <Button 
                onClick={handleSave} 
                className="btn-primary w-full" 
                disabled={saving}
                data-testid="save-metric-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Entry'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <Gauge className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Dashboard</span>
              <span className="sm:hidden">Home</span>
            </TabsTrigger>
            <TabsTrigger value="goals" data-testid="tab-goals">
              <Target className="w-4 h-4 mr-1 sm:mr-2" />
              Goals
            </TabsTrigger>
            <TabsTrigger value="water" data-testid="tab-water">
              <Droplets className="w-4 h-4 mr-1 sm:mr-2" />
              Water
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">
              <TrendingUp className="w-4 h-4 mr-1 sm:mr-2" />
              Logs
            </TabsTrigger>
          </TabsList>

          {/* GOALS TAB */}
          <TabsContent value="goals" className="space-y-6">
            <GoalTracker
              goals={goals}
              onCreateGoal={handleCreateGoal}
              onEditGoal={handleEditGoal}
              onDeleteGoal={handleDeleteGoal}
              loading={loading}
            />
            
            {/* Goals Info Card */}
            <Card className="card-farm bg-primary/5 border-primary/20">
              <CardContent className="py-4">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-foreground mb-1">How Goals Work</p>
                    <p className="text-muted-foreground">
                      Set production targets for eggs, milk, and costs. Progress is calculated automatically 
                      from your logged data. Goals update daily based on your actual farm records.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* DASHBOARD TAB - Calculated Metrics */}
          <TabsContent value="dashboard" className="space-y-6">
            {calculated && (
              <>
                {/* Sustainability Score Card */}
                {calculated.sustainability_score && (
                  <Card className="card-farm" data-testid="sustainability-score">
                    <CardContent className="py-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-heading text-lg font-semibold text-foreground mb-1">
                            Sustainability Score
                          </h3>
                          <p className="text-muted-foreground text-sm">
                            Based on your farm&apos;s actual data
                          </p>
                        </div>
                        <div className={`text-center p-4 rounded-full ${getScoreBg(calculated.sustainability_score.score)}`}>
                          <p className={`font-heading text-4xl font-bold ${getScoreColor(calculated.sustainability_score.score)}`}>
                            {calculated.sustainability_score.score}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            / {calculated.sustainability_score.max}
                          </p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <div className="w-full bg-muted rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full transition-all ${
                              calculated.sustainability_score.score >= 75 ? 'bg-green-500' :
                              calculated.sustainability_score.score >= 50 ? 'bg-emerald-500' :
                              calculated.sustainability_score.score >= 25 ? 'bg-yellow-500' : 'bg-orange-500'
                            }`}
                            style={{ width: `${calculated.sustainability_score.score}%` }}
                          />
                        </div>
                        <p className={`mt-2 text-sm font-medium ${getScoreColor(calculated.sustainability_score.score)}`}>
                          {calculated.sustainability_score.rating}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Daily Water Usage */}
                  <Card className="card-farm" data-testid="metric-water-daily">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Droplets className="w-4 h-4 text-blue-500" />
                        <span className="text-xs text-muted-foreground">Daily Water</span>
                      </div>
                      <p className="font-heading text-2xl font-bold text-foreground">
                        {calculated.water_usage?.daily?.toLocaleString() || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">gallons/day</p>
                    </CardContent>
                  </Card>

                  {/* Monthly Water */}
                  <Card className="card-farm" data-testid="metric-water-monthly">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Droplets className="w-4 h-4 text-blue-500" />
                        <span className="text-xs text-muted-foreground">Monthly Water</span>
                      </div>
                      <p className="font-heading text-2xl font-bold text-foreground">
                        {calculated.water_usage?.monthly?.toLocaleString() || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">gallons/month</p>
                    </CardContent>
                  </Card>

                  {/* Carbon Sequestration */}
                  <Card className="card-farm" data-testid="metric-carbon">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-2 mb-2">
                        <TreeDeciduous className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-muted-foreground">Carbon Offset</span>
                      </div>
                      <p className="font-heading text-2xl font-bold text-foreground">
                        {calculated.carbon_impact?.annual_sequestration?.toLocaleString() || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">lbs CO₂/year</p>
                    </CardContent>
                  </Card>

                  {/* Garden Area */}
                  <Card className="card-farm" data-testid="metric-land">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Sprout className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs text-muted-foreground">Productive Land</span>
                      </div>
                      <p className="font-heading text-2xl font-bold text-foreground">
                        {calculated.land_use?.productive_sqft?.toLocaleString() || 0}
                      </p>
                      <p className="text-xs text-muted-foreground">sq ft</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Production Summary */}
                {(calculated.production?.eggs_weekly > 0 || calculated.production?.milk_weekly > 0) && (
                  <Card className="card-farm" data-testid="production-summary">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-primary" />
                        Weekly Production
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        {calculated.production.eggs_weekly > 0 && (
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                              <Egg className="w-5 h-5 text-amber-500" />
                            </div>
                            <div>
                              <p className="font-heading text-xl font-bold">{calculated.production.eggs_weekly}</p>
                              <p className="text-xs text-muted-foreground">eggs this week</p>
                            </div>
                          </div>
                        )}
                        {calculated.production.milk_weekly > 0 && (
                          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center border">
                              <Milk className="w-5 h-5 text-slate-600" />
                            </div>
                            <div>
                              <p className="font-heading text-xl font-bold">{calculated.production.milk_weekly}</p>
                              <p className="text-xs text-muted-foreground">gallons this week</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Efficiency Metrics */}
                {(calculated.efficiency?.feed_to_egg_ratio || calculated.efficiency?.feed_to_milk_ratio) && (
                  <Card className="card-farm" data-testid="efficiency-metrics">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Gauge className="w-5 h-5 text-primary" />
                        Feed Efficiency
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4">
                        {calculated.efficiency.feed_to_egg_ratio && (
                          <div className="p-3 rounded-lg bg-muted/50">
                            <p className="text-xs text-muted-foreground mb-1">Eggs per $1 Feed</p>
                            <p className="font-heading text-2xl font-bold text-foreground">
                              {calculated.efficiency.feed_to_egg_ratio}
                            </p>
                          </div>
                        )}
                        {calculated.efficiency.feed_to_milk_ratio && (
                          <div className="p-3 rounded-lg bg-muted/50">
                            <p className="text-xs text-muted-foreground mb-1">Gallons per $1 Feed</p>
                            <p className="font-heading text-2xl font-bold text-foreground">
                              {calculated.efficiency.feed_to_milk_ratio}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Empty state for dashboard */}
                {calculated.water_usage?.daily === 0 && calculated.land_use?.productive_sqft === 0 && (
                  <Card className="card-farm">
                    <CardContent className="py-8 text-center">
                      <Leaf className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="font-heading text-lg font-semibold mb-2">No Farm Data Yet</h3>
                      <p className="text-muted-foreground text-sm max-w-md mx-auto">
                        Add garden layouts, livestock, and log production to see your sustainability metrics 
                        calculated automatically from your actual farm data.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* WATER TAB - Detailed Water Breakdown */}
          <TabsContent value="water" className="space-y-6">
            {calculated?.water_usage?.breakdown?.length > 0 ? (
              <>
                <Card className="card-farm" data-testid="water-breakdown">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Droplets className="w-5 h-5 text-blue-500" />
                      Water Usage Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {calculated.water_usage.breakdown.map((item, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                          data-testid={`water-item-${idx}`}
                        >
                          <div>
                            <p className="font-medium text-foreground">{item.source}</p>
                            <p className="text-xs text-muted-foreground">{item.detail}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-heading text-lg font-bold text-blue-500">
                              {item.daily_gallons}
                            </p>
                            <p className="text-xs text-muted-foreground">gal/day</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <div className="flex justify-between items-center">
                        <span className="font-medium text-foreground">Total Daily Usage</span>
                        <span className="font-heading text-2xl font-bold text-blue-500">
                          {calculated.water_usage.daily} gal
                        </span>
                      </div>
                      <div className="flex justify-between items-center mt-2 text-sm">
                        <span className="text-muted-foreground">Monthly Estimate</span>
                        <span className="text-muted-foreground">
                          {calculated.water_usage.monthly?.toLocaleString()} gallons
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Carbon Breakdown */}
                {calculated.carbon_impact?.breakdown?.length > 0 && (
                  <Card className="card-farm" data-testid="carbon-breakdown">
                    <CardHeader className="pb-2">
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <TreeDeciduous className="w-5 h-5 text-green-500" />
                        Carbon Impact Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {calculated.carbon_impact.breakdown.map((item, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                          >
                            <div>
                              <p className="font-medium text-foreground">{item.source}</p>
                              <p className="text-xs text-muted-foreground">{item.detail}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-heading text-lg font-bold text-green-500">
                                {item.lbs_co2_year}
                              </p>
                              <p className="text-xs text-muted-foreground">lbs CO₂/yr</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="card-farm">
                <CardContent className="py-8 text-center">
                  <Droplets className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-heading text-lg font-semibold mb-2">No Water Data</h3>
                  <p className="text-muted-foreground text-sm">
                    Add garden layouts and livestock to see water usage calculations.
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* LOGS TAB - Manual Entries */}
          <TabsContent value="logs" className="space-y-6">
            {/* Manual Entry Summary Cards */}
            {summary.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {summary.map((item) => {
                  const info = getMetricInfo(item.metric_type);
                  const IconComponent = info.icon;
                  
                  return (
                    <Card key={item.metric_type} className="card-farm" data-testid={`summary-${item.metric_type}`}>
                      <CardContent className="py-4">
                        <div className="flex items-center gap-2 mb-2">
                          <IconComponent className="w-4 h-4 text-primary" />
                          <span className="text-xs text-muted-foreground">{info.name}</span>
                        </div>
                        <p className="font-heading text-2xl font-bold text-foreground">
                          {item.total.toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.unit} total • {item.entries} entries
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Recent Entries */}
            <Card className="card-farm" data-testid="recent-entries">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg">Manual Log Entries</CardTitle>
              </CardHeader>
              <CardContent>
                {metrics.length > 0 ? (
                  <div className="space-y-2">
                    {metrics.slice(0, 10).map((metric) => {
                      const info = getMetricInfo(metric.metric_type);
                      const IconComponent = info.icon;
                      
                      return (
                        <div 
                          key={metric.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                          data-testid={`metric-entry-${metric.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                              <IconComponent className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-foreground">
                                {metric.value} {metric.unit}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {info.name} • {formatDate(metric.date)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {metric.notes && (
                              <span className="text-xs text-muted-foreground max-w-[100px] truncate hidden sm:block">
                                {metric.notes}
                              </span>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(metric.id)}
                              className="text-muted-foreground hover:text-destructive"
                              data-testid={`delete-metric-${metric.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Leaf className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No manual entries yet. Click &quot;Log Entry&quot; to add data.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Info Note */}
            <Card className="card-farm bg-primary/5 border-primary/20">
              <CardContent className="py-4">
                <div className="flex gap-3">
                  <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-foreground mb-1">About Manual Logging</p>
                    <p className="text-muted-foreground">
                      Log compost, feed costs, and other metrics here. These entries contribute to your 
                      sustainability score and help calculate feed efficiency ratios.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default SustainabilityTracker;
